/**
 * Centralized confirm dialog using Shadcn AlertDialog.
 * Replaces `window.confirm` across the app for consistent UX (iter39 P4).
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   const ok = await confirm({ title: "Eliminare?", description: "...", destructive: true });
 *   if (ok) ...
 *   // ...and render <ConfirmDialog /> once near the top of the component tree.
 */
import { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const defaults = {
  title: "Confermi l'azione?",
  description: "",
  confirmLabel: "Conferma",
  cancelLabel: "Annulla",
  destructive: false,
};

export function useConfirm() {
  const [state, setState] = useState({ open: false, ...defaults });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, ...defaults, ...opts });
    });
  }, []);

  const handleClose = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const ConfirmDialog = () => (
    <AlertDialog open={state.open} onOpenChange={(open) => !open && handleClose(false)}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="confirm-dialog-title">{state.title}</AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription data-testid="confirm-dialog-description">{state.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handleClose(false)} data-testid="confirm-dialog-cancel">{state.cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handleClose(true)}
            className={state.destructive ? "bg-rose-600 hover:bg-rose-700" : ""}
            data-testid="confirm-dialog-confirm"
          >
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, ConfirmDialog };
}
